import { brl, parseMoney } from "@/lib/api";
import { Button, Card, Checkbox, Input } from "@/components/ui-kit";
import { Landmark, AlertTriangle } from "lucide-react";

const dt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR") : "—");

/**
 * Duplicatas da NF-e viradas contas a pagar.
 *
 * Fecha o buraco entre a compra e o caixa: antes, a mercadoria entrava no
 * estoque e a dívida ficava só no boleto em cima do balcão. O Financeiro não
 * sabia que ela existia até alguém digitar à mão — e conta a pagar que ninguém
 * digitou é conta a pagar que vence esquecida.
 *
 * Tudo editável, porque a nota nem sempre bate com o combinado: o fornecedor
 * emite com 30/60 e o vendedor prometeu 45, ou a parcela veio com centavo de
 * diferença. Corrigir aqui é mais barato que corrigir depois no Financeiro.
 */
export default function ImportBills({ bills, nfe, onChange, total }) {
  if (!bills) return null;

  const marcadas = bills.filter((b) => b.lancar);
  const soma = marcadas.reduce((s, b) => s + parseMoney(b.amount), 0);
  // A soma das parcelas deveria bater com a nota. Quando não bate, quase sempre
  // é parcela editada — vale avisar antes de virar dívida errada no Financeiro.
  const divergente = total > 0 && marcadas.length > 0 && Math.abs(soma - total) >= 0.01;

  if (bills.length === 0) {
    return (
      <Card className="p-4 flex items-start gap-3 text-sm" data-testid="stock-import-bills-empty">
        <Landmark size={18} className="text-slate-400 shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="font-medium text-slate-700">Nota sem duplicatas</p>
          <p className="text-xs text-slate-500 mt-0.5">
            O XML não traz parcelas (compra à vista, ou o fornecedor não as informou).
            Nada será lançado em contas a pagar — se houver boleto, lance em Financeiro.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4" data-testid="stock-import-bills">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="flex items-center gap-2 font-medium text-slate-900">
          <Landmark size={18} className="text-brand-600" aria-hidden />
          Boletos da nota
          <span className="text-xs font-normal text-slate-500">
            {nfe?.number ? `NF-e ${nfe.number}` : ""}
            {bills.length > 1 ? ` · ${bills.length} parcelas` : " · parcela única"}
          </span>
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">
            {marcadas.length > 0
              ? <>Vai lançar <b className="font-mono">{brl(soma)}</b> em contas a pagar</>
              : <span className="text-slate-400">Nenhuma parcela será lançada</span>}
          </span>
          <Button variant="link" size="sm"
            onClick={() => onChange(bills.map((b) => ({ ...b, lancar: marcadas.length === 0 })))}
            data-testid="stock-import-bills-toggle-all">
            {marcadas.length === 0 ? "Marcar todas" : "Desmarcar todas"}
          </Button>
        </div>
      </div>

      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {bills.map((b, i) => (
          <li key={i} className={`flex flex-wrap items-center gap-3 px-3 py-2.5 ${b.lancar ? "" : "opacity-50"}`}
            data-testid="stock-import-bill">
            <Checkbox
              checked={b.lancar}
              onChange={(e) => onChange(bills.map((x, j) => (j === i ? { ...x, lancar: e.target.checked } : x)))}
              label={`Parcela ${b.number}`}
              aria-label={`Lançar a parcela ${b.number} em contas a pagar`}
              className="text-sm text-slate-700 min-w-[8rem]"
            />
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`venc-${i}`}>Vencimento</label>
                <Input id={`venc-${i}`} type="date" size="sm" value={b.due_date || ""}
                  onChange={(e) => onChange(bills.map((x, j) => (j === i ? { ...x, due_date: e.target.value } : x)))}
                  disabled={!b.lancar} data-testid="stock-import-bill-due" className="text-sm" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`valor-${i}`}>Valor</label>
                <Input id={`valor-${i}`} type="number" step="0.01" size="sm" mono value={b.amount}
                  onChange={(e) => onChange(bills.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                  disabled={!b.lancar} data-testid="stock-import-bill-amount" className="w-28 text-sm" />
              </div>
            </div>
            {b.due_date && b.lancar && (
              <span className="text-xs text-slate-400">vence {dt(b.due_date)}</span>
            )}
          </li>
        ))}
      </ul>

      {divergente && (
        <p className="flex items-start gap-2 text-xs text-amber-700 mt-2.5" data-testid="stock-import-bills-diff">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" aria-hidden />
          A soma das parcelas ({brl(soma)}) não bate com o total da nota ({brl(total)}).
          Confira antes de confirmar — vai virar dívida com esse valor.
        </p>
      )}

      {/* Reimportar o mesmo XML é comum (o operador se perde e recomeça). Dizer
          que isso não duplica evita o medo de confirmar duas vezes. */}
      <p className="text-xs text-slate-400 mt-2">
        As parcelas entram em <b>Financeiro › Contas a pagar</b>, no nome do fornecedor.
        Reimportar a mesma nota não lança de novo.
      </p>
    </Card>
  );
}
